import { useId } from 'react';

import { fieldProgressJourney } from '../field-progress-journey';

import type { FieldSnapshot } from '@mio/shared';
import type { CSSProperties } from 'react';

import './field-progress-button.css';

interface FieldProgressButtonProps {
  snapshot: FieldSnapshot;
  active: boolean;
  onClick: () => void;
}

export function FieldProgressButton({ snapshot, active, onClick }: FieldProgressButtonProps) {
  const journey = fieldProgressJourney(snapshot);
  const hasProgress = Boolean(snapshot.progress);
  const descriptionId = useId();
  const recheck = journey.milestones.filter((milestone) => milestone.state === 'needs_recheck');
  const waiting = journey.milestones.filter((milestone) => milestone.state === 'waiting');
  const status = recheck.length
    ? `Needs another check: ${recheck.map((milestone) => milestone.label).join(', ')}.`
    : waiting.length
      ? `Waiting for confirmation: ${waiting.map((milestone) => milestone.label).join(', ')}.`
      : '';

  return (
    <button
      className="field-progress-button"
      type="button"
      aria-label="Progress"
      aria-current={active ? 'page' : undefined}
      aria-describedby={descriptionId}
      onClick={onClick}
    >
      <span className="field-progress-button-heading" aria-hidden="true">
        <span>Progress</span>
        {hasProgress ? (
          <span className="field-progress-button-count">
            {journey.completedCount}/{journey.milestones.length}
          </span>
        ) : null}
      </span>
      {hasProgress ? (
        <span className="field-progress-button-track" aria-hidden="true">
          {journey.milestones.map((milestone) => (
            <span
              key={milestone.stage}
              className="field-progress-button-segment"
              data-state={milestone.state}
              style={
                {
                  '--progress-fill': `${(milestone.verified / milestone.total) * 100}%`,
                } as CSSProperties
              }
            >
              <span />
            </span>
          ))}
        </span>
      ) : null}
      <span className="sr-only" id={descriptionId}>
        {hasProgress
          ? `${journey.completedCount} of ${journey.milestones.length} milestones complete. ${status}`
          : 'Progress is not available yet.'}
      </span>
    </button>
  );
}
